import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { FileText, TrendingUp, Heart, Car, Users, Home } from "lucide-react";
import ArticleCard from "../components/ArticleCard";
import SEOHead from "../components/SEOHead";
import Breadcrumbs from "../components/Breadcrumbs";
import AdSensePlaceholder from "../components/AdSensePlaceholder";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  administratif: FileText,
  finance: TrendingUp,
  sante: Heart,
  auto: Car,
  famille: Users,
  "vie-pratique": Home,
};

export default function CategoryPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    axios
      .get(`${API}/categories/${slug}`)
      .then((res) => setData(res.data))
      .catch(() => setError("Catégorie introuvable."))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-1/3" />
          <div className="h-4 bg-slate-200 rounded w-2/3" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-72 bg-slate-200 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        data-testid="category-error"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center"
      >
        <p className="text-slate-500 text-lg">{error}</p>
        <Link to="/" className="mt-4 inline-block text-blue-600 hover:underline">
          Retour à l'accueil
        </Link>
      </div>
    );
  }

  const category = data?.category || {};
  const articles = Array.isArray(data?.articles) ? data.articles : [];
  const Icon = CATEGORY_ICONS[slug] || FileText;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: category.label,
      description: category.description,
      url: `https://marocpratique.com/categorie/${slug}`,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Accueil", item: "https://marocpratique.com" },
        { "@type": "ListItem", position: 2, name: category.label, item: `https://marocpratique.com/categorie/${slug}` },
      ],
    },
  ];

  return (
    <>
      <SEOHead
        title={`${category.label} au Maroc : Guides Pratiques & Conseils | MarocPratique`}
        description={`Tous les guides pratiques sur ${category.label.toLowerCase()} au Maroc. ${category.description}`}
        canonical={`https://marocpratique.com/categorie/${slug}`}
        type="website"
        jsonLd={jsonLd}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Breadcrumbs
          items={[{ label: category.label, href: `/categorie/${slug}` }]}
        />

        {/* Category Header */}
        <header
          data-testid="category-header"
          className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 mb-8 flex items-start gap-4"
        >
          <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
            <Icon className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mb-1">
              {category.label}
            </h1>
            <p className="text-slate-500 text-base">{category.description}</p>
            <p className="text-sm text-blue-600 font-medium mt-2">
              {category.article_count} article{category.article_count !== 1 ? "s" : ""}
            </p>
          </div>
        </header>

        <AdSensePlaceholder position="banner" />

        {/* Articles Grid */}
        <section data-testid="category-articles">
          {articles.length === 0 ? (
            <div className="text-center py-16 text-slate-500">
              <p>Aucun article dans cette catégorie pour le moment.</p>
              <Link to="/" className="mt-4 inline-block text-blue-600 hover:underline">
                Voir tous les articles
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-12">
              {articles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          )}
        </section>
      </div>
    </>
  );
}
